import React, { FC, FormEvent, FormHTMLAttributes } from "react";
import { useSelector } from "react-redux";
import { ReduxState } from "~/types/redux";
import AutoComplete from "./AutoComplete";
import ControlledAutoComplete from "./ControlledAutoComplete";
import Button from "../Button";
import AddressFormStyled from "./AddressFormStyled";
import { Address } from "../../types/address";
import Location from "../Icons/fill/Location";
import { ArrowRight } from "../Icons/fill";
import InjectLegalAndTooltips from "../InjectLegalAndTooltips";
import useWindowSize from "~/hooks/useWindowSize";

interface Props extends FormHTMLAttributes<HTMLFormElement> {
  apiVersion: string;
  selectedAddress: Address;
  setSelectedAddress: (address: Address) => void;
  handleInputChange: (event: FormEvent<HTMLInputElement>) => void;
  buttonState: {
    disabled: boolean;
    loading: boolean;
  };
  onSubmit: (event: FormEvent) => void;
  placeholder?: string;
  placeholderMobile?: string;
  hideIcon?: boolean;
  buttonText?: string;
  useIconButton?: boolean;
  addressCheckDisclaimer?: string;
}

const AddressForm: FC<Props> = ({
  apiVersion,
  selectedAddress,
  setSelectedAddress,
  buttonState,
  onSubmit,
  placeholder,
  placeholderMobile,
  hideIcon,
  buttonText,
  useIconButton,
  addressCheckDisclaimer,
  ...props
}) => {
  const onKeyDown = (e) => !buttonState.disabled && e.keyCode === 13 && onSubmit(e);
  const { tablet, desktop, xlDesktop, xxlDesktop } = useWindowSize();
  const isBigScreen = tablet || desktop || xlDesktop || xxlDesktop;

  const { isVerizonKiosk, isVerizonDiscovery } = useSelector((store: ReduxState) => store);

  const getButtonText = () => {
    if (useIconButton) return <ArrowRight />;
    if (buttonText) return buttonText;

    return isBigScreen ? "Check availability" : "Go";
  };

  const buttonClassNames = isBigScreen
    ? "address-form__button address-form__button__tablet-up"
    : "address-form__button__mobile";

  return (
    <AddressFormStyled {...props} onKeyDown={onKeyDown} title={selectedAddress?.label}>
      <div className="address-form__container">
        {!hideIcon && <Location />}
        {isVerizonKiosk || isVerizonDiscovery ? (
          <ControlledAutoComplete
            selectedAddress={selectedAddress}
            setSelectedAddress={setSelectedAddress}
            placeholder={isBigScreen ? placeholder : placeholderMobile}
            apiVersion={apiVersion}
            submitCallBack={onSubmit}
          />
        ) : (
          <AutoComplete
            selectedAddress={selectedAddress}
            setSelectedAddress={setSelectedAddress}
            placeholder={isBigScreen ? placeholder : placeholderMobile}
            apiVersion={apiVersion}
          />
        )}
        <Button
          {...buttonState}
          className={buttonClassNames}
          type="submit"
          onClick={onSubmit}
          data-testid={isBigScreen ? "btn-check-address" : "btn-check-address-mobile"}
          data-chsn-reward-click="cart service check"
        >
          {getButtonText()}
        </Button>
      </div>
      {addressCheckDisclaimer && !isVerizonKiosk && !isVerizonDiscovery && (
        <div className="address-form__addressCheckDisclaimer">
          <InjectLegalAndTooltips copy={addressCheckDisclaimer} />
        </div>
      )}
    </AddressFormStyled>
  );
};

AddressForm.displayName = "AddressForm";

export default AddressForm;
